import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Shield, Scan, User, Lock, Mail, Phone, Calendar } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
import { GAM_TICKET_LOGO } from '@/config/assets';
import { STADIUM_BG } from '@/config/assets';

export default function UnifiedLoginPage() {
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('customer');
  const [customerLoginMethod, setCustomerLoginMethod] = useState('email');
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API_URL}/api/auth/login`, {
        identifier,
        password
      });

      const { token, role, user } = response.data;

      if (role === 'admin') {
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify({ ...user, isAdmin: true }));
        toast.success(`Welcome, Admin ${user.username}!`);
        window.location.href = '/dashboard';
      } else if (role === 'staff') {
        localStorage.setItem('staff_token', token);
        localStorage.setItem('staff_user', JSON.stringify(user));
        toast.success(`Welcome, ${user.name}!`);
        window.location.href = '/staff/scanner';
      } else {
        localStorage.setItem('user_token', token);
        localStorage.setItem('user_data', JSON.stringify(user));
        toast.success(`Welcome, ${user.name}!`);
        // Check if there's a redirect URL stored (e.g., from trying to buy tickets)
        const redirectUrl = sessionStorage.getItem('redirectAfterLogin');
        if (redirectUrl) {
          sessionStorage.removeItem('redirectAfterLogin');
          window.location.href = redirectUrl;
        } else {
          // Stay on landing page to continue browsing
          window.location.href = '/';
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  const getPlaceholder = () => {
    switch (activeTab) {
      case 'admin':
        return 'Admin username';
      case 'staff':
        return 'Staff username';
      default:
        return customerLoginMethod === 'email' ? 'Email address' : 'Phone number (+220...)';
    }
  };

  const getIcon = () => {
    switch (activeTab) {
      case 'admin':
        return <Shield className="w-6 h-6 text-white" />;
      case 'staff':
        return <Scan className="w-6 h-6 text-white" />;
      default:
        return <User className="w-6 h-6 text-white" />;
    }
  };

  const getColor = () => {
    switch (activeTab) {
      case 'admin':
        return 'from-red-600 to-red-700';
      case 'staff':
        return 'from-purple-600 to-purple-700';
      default:
        return 'from-[#0055FF] to-[#0044CC]';
    }
  };

  const getButtonColor = () => {
    switch (activeTab) {
      case 'admin':
        return 'bg-red-600 hover:bg-red-700';
      case 'staff':
        return 'bg-purple-600 hover:bg-purple-700';
      default:
        return 'bg-[#0055FF] hover:bg-[#0044CC]';
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Full Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${STADIUM_BG})` }}
      >
        <div className="absolute inset-0 bg-black/50" />
      </div>

      {/* Content */}
      <div className="relative z-10 min-h-screen flex flex-col">
        {/* Header */}
        <header className="p-6 flex items-center justify-between">
          <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-white/30 shadow-xl mx-auto">
            <img src={GAM_TICKET_LOGO} alt="Gam Ticket" className="w-full h-full object-cover" />
          </div>
          <Link 
            to="/events" 
            className="flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-white text-sm font-medium hover:bg-white/20 transition-colors"
          >
            <Calendar className="w-4 h-4" />
            Browse Events
          </Link>
        </header>

        {/* Centered Login Card */}
        <main className="flex-1 flex items-center justify-center px-6 pb-10">
          <Card className="w-full max-w-md bg-white/95 backdrop-blur-xl shadow-2xl border-0 rounded-2xl overflow-hidden">
            <CardHeader className="text-center pb-2 pt-6">
              <CardTitle className="text-2xl font-bold text-slate-800">Welcome to Gam Ticket</CardTitle>
              <CardDescription className="text-slate-500">Sign in to your account</CardDescription>
            </CardHeader>
            
            <CardContent className="pb-6">
              {/* Role Tabs */}
              <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); setIdentifier(''); }} className="mb-5">
                <TabsList className="grid w-full grid-cols-3 bg-slate-100 p-1 rounded-xl">
                  <TabsTrigger 
                    value="customer" 
                    className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm text-xs"
                  >
                    <User className="w-3.5 h-3.5 mr-1" />
                    Customer
                  </TabsTrigger>
                  <TabsTrigger 
                    value="staff" 
                    className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm text-xs"
                  >
                    <Scan className="w-3.5 h-3.5 mr-1" />
                    Staff
                  </TabsTrigger>
                  <TabsTrigger 
                    value="admin" 
                    className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm text-xs"
                  >
                    <Shield className="w-3.5 h-3.5 mr-1" />
                    Admin
                  </TabsTrigger>
                </TabsList>
              </Tabs>

              {/* Role Icon */}
              <div className={`w-14 h-14 bg-gradient-to-br ${getColor()} rounded-xl flex items-center justify-center mx-auto mb-5 shadow-lg transition-all duration-300`}>
                {getIcon()}
              </div>

              <form onSubmit={handleLogin} className="space-y-4">
                {/* Customer Email/Phone Toggle */}
                {activeTab === 'customer' && (
                  <div className="flex gap-2 p-1 bg-slate-100 rounded-xl">
                    <button
                      type="button"
                      onClick={() => { setCustomerLoginMethod('email'); setIdentifier(''); }}
                      className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                        customerLoginMethod === 'email' 
                          ? 'bg-white shadow-sm text-slate-800' 
                          : 'text-slate-500 hover:text-slate-700'
                      }`}
                    >
                      <Mail className="w-4 h-4" />
                      Email
                    </button>
                    <button
                      type="button"
                      onClick={() => { setCustomerLoginMethod('phone'); setIdentifier(''); }}
                      className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                        customerLoginMethod === 'phone' 
                          ? 'bg-white shadow-sm text-slate-800' 
                          : 'text-slate-500 hover:text-slate-700'
                      }`}
                    >
                      <Phone className="w-4 h-4" />
                      Phone
                    </button>
                  </div>
                )}

                <div>
                  <label className="text-sm font-medium mb-1.5 block text-slate-700">
                    {activeTab === 'customer' 
                      ? (customerLoginMethod === 'email' ? 'Email Address' : 'Phone Number')
                      : 'Username'}
                  </label>
                  <div className="relative">
                    {activeTab === 'customer' ? (
                      customerLoginMethod === 'email' ? (
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      ) : (
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      )
                    ) : (
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    )}
                    <Input
                      type={activeTab === 'customer' && customerLoginMethod === 'email' ? 'email' : 'text'}
                      placeholder={getPlaceholder()}
                      value={identifier}
                      onChange={(e) => setIdentifier(e.target.value)}
                      className="pl-10 h-11 rounded-xl border-slate-200 focus:border-[#0055FF] focus:ring-[#0055FF]/20"
                      required
                      data-testid="login-identifier"
                    />
                  </div>
                </div>
                
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="text-sm font-medium text-slate-700">Password</label>
                    {activeTab === 'customer' && (
                      <Link 
                        to="/forgot-password" 
                        className="text-xs text-[#0055FF] hover:underline"
                        data-testid="forgot-password-link"
                      >
                        Forgot password?
                      </Link>
                    )}
                  </div>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      type="password"
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-10 h-11 rounded-xl border-slate-200 focus:border-[#0055FF] focus:ring-[#0055FF]/20"
                      required
                      data-testid="login-password"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  className={`w-full h-11 rounded-xl ${getButtonColor()} transition-all duration-300 font-semibold shadow-lg hover:shadow-xl hover:-translate-y-0.5`}
                  disabled={loading}
                  data-testid="login-submit"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    `Sign in as ${activeTab.charAt(0).toUpperCase() + activeTab.slice(1)}`
                  )}
                </Button>
              </form>

              {activeTab === 'customer' && (
                <div className="mt-5 text-center">
                  <p className="text-slate-500 text-sm">
                    Don't have an account?{' '}
                    <Link to="/user-register" className="text-[#0055FF] hover:underline font-semibold">
                      Register here
                    </Link>
                  </p>
                </div>
              )}

              {activeTab !== 'customer' && (
                <p className="mt-4 text-xs text-center text-slate-400">
                  {activeTab === 'admin' 
                    ? 'Admin accounts are created by system administrators only.'
                    : 'Staff accounts are created by administrators.'}
                </p>
              )}
            </CardContent>
          </Card>
        </main>

        {/* Footer */}
        <footer className="p-4 text-center">
          <p className="text-white/60 text-sm">
            © 2026 Gam Ticket. The Premier Ticketing Platform for The Gambia.
          </p>
        </footer>
      </div>
    </div>
  );
}
